import { type ComponentType, memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { type ClipDiscussion } from '~/components/Annotations/shared/types';
import {
  AnnotationAvatarWithComment,
  type AnnotationAvatarWithCommentProps,
} from '~/components/AnnotationsV2/AnnotationsAvatarsContainer/AnnotationAvatarContainer/AnnotationAvatarWithComment';
import {
  type AnnotationAvatarsFetcherProps,
  type BaseAnnotationsAvatarsProps,
} from '~/components/AnnotationsV2/AnnotationsAvatarsContainer/shared/annotationAvatarsTypes';
import { getAnnotatedDiscussions } from '~/components/AnnotationsV2/AnnotationsAvatarsContainer/shared/annotationsAvatarsUtils';
import { useAnnotationV2Store } from '~/components/AnnotationsV2/providers/AnnotationV2Provider';
import { useDiscussionPanelContextSelector } from '~/components/AssetModal/shared/context/DiscussionsPanelContext/DiscussionsPanelContext';
import { discussionsFilterSelector } from '~/components/AssetModal/shared/context/DiscussionsPanelContext/discussionsPanelContextUtils';
import { centralizedClipVersionNumberSelector } from '~/store/centralizedClip/selectors';
import { activeDiscussionIdParamSelector } from '~/store/router/selectors';

export type AnnotationsAvatarsContainerProps = {
  pageNumber: number;
  timestamp?: number;
  hideAnnotations?: boolean;
  /** The surface's annotations-avatars implementation — fetches discussions its own way and calls renderAnnotations with them. */
  AnnotationsAvatars: ComponentType<AnnotationAvatarsFetcherProps>;
} & Pick<AnnotationAvatarWithCommentProps, 'AnnotationComment'> &
  BaseAnnotationsAvatarsProps;

/**
 * V2: AnnotationsAvatarsContainer that supports multiple annotations per discussion
 * Uses V2 utilities and components for rendering
 */
export const AnnotationsAvatarsContainer = memo(
  ({
    containerWidth,
    containerHeight,
    containerLeft,
    containerTop,
    onAvatarClick,
    pageNumber,
    timestamp,
    hideAnnotations,
    AnnotationsAvatars,
    AnnotationComment,
  }: AnnotationsAvatarsContainerProps) => {
    const activeDiscussionId = useSelector(activeDiscussionIdParamSelector);

    const versionNumber = useSelector(centralizedClipVersionNumberSelector);
    const isAnnotationsEnabled = useAnnotationV2Store((state) => state.isAnnotationsEnabled);

    const discussionsFilter = useDiscussionPanelContextSelector(discussionsFilterSelector);

    const renderAnnotations: AnnotationAvatarsFetcherProps['renderAnnotations'] = useCallback(
      ({ canReply, canShowMentionsButton, canDelete, discussions, canEdit, canResolve }) => {
        const activeDiscussion = activeDiscussionId ? discussions.find((d) => d.id === activeDiscussionId) : null;

        let annotatedDiscussions: ClipDiscussion[] = [];

        if (!hideAnnotations && (isAnnotationsEnabled || activeDiscussion)) {
          // V2: Use V2 getAnnotatedDiscussions which handles annotations array
          annotatedDiscussions = getAnnotatedDiscussions({
            discussions: activeDiscussion ? [activeDiscussion] : discussions,
            timestamp,
            pageNumber,
            assetVersion: versionNumber,
            filter: discussionsFilter,
          });
        }

        return (
          <div className="relative" style={{ width: containerWidth, height: containerHeight }}>
            {annotatedDiscussions.map((discussion) => (
              <AnnotationAvatarWithComment
                AnnotationComment={AnnotationComment}
                containerLeft={containerLeft}
                containerTop={containerTop}
                containerHeight={containerHeight}
                containerWidth={containerWidth}
                onAvatarClick={onAvatarClick}
                discussion={discussion}
                key={discussion.id}
                canReply={canReply}
                canDelete={canDelete}
                canEdit={canEdit}
                canResolve={canResolve}
                canShowMentionsButton={canShowMentionsButton}
              />
            ))}
          </div>
        );
      },
      [
        activeDiscussionId,
        AnnotationComment,
        hideAnnotations,
        isAnnotationsEnabled,
        containerWidth,
        containerHeight,
        timestamp,
        pageNumber,
        versionNumber,
        discussionsFilter,
        containerLeft,
        containerTop,
        onAvatarClick,
      ],
    );

    return <AnnotationsAvatars renderAnnotations={renderAnnotations} />;
  },
);

AnnotationsAvatarsContainer.displayName = 'AnnotationsAvatarsContainer';
